import { z } from 'zod';

export function ucfirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.substring(1);
}

/**
 * Initials from a display name (e.g. for avatars). Strips parentheticals so
 * suffixes like "(Contractor)" do not become spurious initials, and uses the
 * first letter of each hyphenated segment (e.g. "Mary-Jane" → "MJ").
 */
export function getInitials(input: string, length: number): string {
  const withoutParens = input
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return withoutParens
    .split(/\s+/)
    .map((word) =>
      word
        .split('-')
        .map((part) => {
          const m = part.match(/[a-zA-Z]/);
          return m ? m[0].toUpperCase() : '';
        })
        .join(''),
    )
    .join('')
    .substring(0, length);
}

export const sortingCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export const ARN_REGEX = /^arn:(aws[a-zA-Z-]*)?:[a-z0-9-]+:[a-z0-9-]*:[0-9]*:[^:\s]+$/;
export const arnStringSchema = z
  .string()
  .refine((val) => ARN_REGEX.test(val), { message: 'Please provide a valid ARN.' });

// A more permissive alternative to zod's
export const EMAIL_REGEX = /^[^\s@]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
export const emailStringSchema = z
  .string()
  .regex(EMAIL_REGEX, { message: 'Please provide a valid email address.' });

export const isValidEmailAddress = (email: string) => EMAIL_REGEX.test(email);

// This is a super basic check. Our serials are in a few different formats, but they are always 3 chunks of alphanumerics split with hyphens
export const SERIAL_NUMBER_REGEX = /^[^-]+-[^-]+-[^-]+$/;
export const serialNumberSchema = z
  .string()
  .regex(SERIAL_NUMBER_REGEX, { message: 'Please provide a valid serial number.' });

export const isSerialNumber = (s: string) => SERIAL_NUMBER_REGEX.test(s);

/**
 * Return word with the proper article (a or an) in front of it.
 * @param word
 */
export function withArticle(word: string) {
  const vowelSound = /^[aeiou]/i.test(word);
  return `${vowelSound ? 'an' : 'a'} ${word}`;
}

export function lcfirst(input: string): string {
  return input.charAt(0).toLowerCase() + input.substring(1);
}

// This map is used in the pluralize function below
const commonNonStandardPluralizations = new Map<string, string>([
  ['this', 'these'],
  ['goose', 'geese'],
  ['switch', 'switches'],
]);

/**
 * Pluralizes a string based on a count
 * @param count
 * @param input The string to pluralize. Can start with "this", which will be pluralized to "these"
 * @param plural The plural form of the string, defaults to `input + "s"`
 *
 * @example pluralize(2, 'VLAN') --> 'VLANs'
 */
export function pluralize(count: number, input: string, plural?: string): string {
  if (count === -1 || count === 1) {
    return input;
  }

  if (input === '') return input;
  if (plural) return plural;
  const checkString = input.toLowerCase();
  const inputIsCapitalized = input[0]?.toLowerCase() !== input[0];
  const pluralizedString = commonNonStandardPluralizations.get(checkString);
  if (pluralizedString) {
    return inputIsCapitalized ? ucfirst(pluralizedString) : pluralizedString;
  }
  return `${input}s`;
}

export function snakeOrKebabCaseToWords(input: string): string {
  return ucfirst(input.toLowerCase().replaceAll(/[_-]+/g, ' '));
}

const ACRONYMS = new Set([
  'AP',
  'BGP',
  'CPU',
  'DHCP',
  'DSL',
  'DNS',
  'ICMP',
  'IP',
  'IPsec',
  'ISP',
  'SSID',
  'STP',
  'TCP',
  'UDP',
  'UUID',
  'VLAN',
  'VPN',
  'WAN',
]);

export function screamingSnakeOrKebabCaseToWords(input: string): string;
export function screamingSnakeOrKebabCaseToWords(input: string | null): string | null;
export function screamingSnakeOrKebabCaseToWords(input: string | undefined): string | undefined;
export function screamingSnakeOrKebabCaseToWords(
  input: string | null | undefined,
): string | null | undefined;
export function screamingSnakeOrKebabCaseToWords(
  input: string | null | undefined,
): string | null | undefined {
  if (input == null) return input;
  if (ACRONYMS.has(input)) return input;
  return ucfirst(input.toLowerCase().replaceAll(/[_-]+/g, ' '));
}

export function titleCase(input: string): string {
  return input
    .toLowerCase()
    .replaceAll(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => ucfirst(word))
    .join(' ');
}

export const NON_NUMERIC_NON_PUNCTUATION_EXCEPT_HYPHEN_AND_SPACE_REGEX = /^[\p{L} -]{2,}$/u;

export function capitalizeCommonAcronyms(input: string): string {
  let s = input;

  for (const acronym of ACRONYMS) {
    s = s.replace(RegExp(`\\b${acronym}\\b`, 'gi'), acronym);
  }

  return s;
}

export const dayOfWeek = (num: number) =>
  ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][num];

export function hourOfDay(hour: number): string {
  const v = hour % 12;
  const m = hour >= 12 ? 'PM' : 'AM';
  return v === 0 ? `12${m}` : `${v.toString()}${m}`;
}

const ORDINAL_SUFFIXES = ['th', 'st', 'nd', 'rd'] as const;

export function numberToOrdinal(num: number): string {
  const v = num % 100;
  return num + (ORDINAL_SUFFIXES[(v - 20) % 10] || ORDINAL_SUFFIXES[v] || ORDINAL_SUFFIXES[0]);
}

// src: https://gist.github.com/codeguy/6684588?permalink_comment_id=4325476#gistcomment-4325476
export function sluggify(text: string) {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
}

// Note this is not the inverse of sluggify
export function unSlug(s: string) {
  return s
    .split('_')
    .map((word) => ucfirst(word))
    .join(' ');
}

export function humanizeWordmark(str: string): string {
  const exceptions = new Map<string, string>([
    ['activcard', 'ActivCard'],
    ['actividentity', 'ActivIdentity'],
    ['activeperl', 'ActivePerl'],
    ['activepython', 'ActivePython'],
    ['activestate', 'ActiveState'],
    ['advancestack', 'AdvanceStack'],
    ['apple, inc.', 'Apple'],
    ['bomarchivehelper', 'BOMArchiveHelper'],
    ['cfnetwork', 'CFNetwork'],
    ['ical', 'iCal'],
    ['ichat', 'iChat'],
    ['iphoto', 'iPhoto'],
    ['itunes', 'iTunes'],
    ['intel corporate', 'Intel'],
    ['omniaccess', 'OmniAccess'],
    ['securecomputing', 'Secure Computing'],
    ['tippingpoint', 'TippingPoint'],
  ]);

  const englishWords = new Set<string>([
    'a',
    'all',
    'air',
    'and',
    'any',
    'at',
    'by',
    'for',
    'how',
    'in',
    'on',
    'or',
    'out',
    'the',
    'up',
    'why',
  ]);

  return str
    .toLowerCase()
    .split(/[_-]/)
    .map((word) => {
      if (exceptions.has(word)) {
        return exceptions.get(word) as string;
      }
      const transformedWord = word.replace(/(\d)([a-z])/g, (_, p1, p2) => p1 + p2.toUpperCase());
      if (transformedWord.length < 4 && !englishWords.has(transformedWord)) {
        return transformedWord.toUpperCase();
      }
      return transformedWord.charAt(0).toUpperCase() + transformedWord.slice(1);
    })
    .join(' ');
}
